import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { Router, NavigationStart, ActivatedRoute } from '@angular/router';

import {
  debounceTime,
  distinctUntilChanged,
  Observable,
  Subscription,
  switchMap,
} from 'rxjs';
import { OfferService } from '../../service/offer.service';
import { MatChipsModule } from '@angular/material/chips';
import { LocationSuggestion, OfferWithImage } from '@/model/offer-with-image';
import { Category } from '@/model/category';
import { getTagInfoFromUrlParams, TagInfo } from '@/utils/search-query-params';

interface Suggestions {
  offers: OfferWithImage[];
  categories: Category[];
  locations: LocationSuggestion[];
}

interface Suggestion {
  id: string;
  name: string;
}

@Component({
  selector: 'app-search',
  imports: [
    ReactiveFormsModule,
    MatIcon,
    MatFormField,
    MatInput,
    MatChipsModule
],
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  standalone: true,
})
export class SearchComponent implements OnInit, OnDestroy {
  name = new FormControl('');
  tags: TagInfo[] = [];
  suggestions: Suggestions = { offers: [], categories: [], locations: [] };
  private routerSubscription!: Subscription;
  private searchRoutes = ['/search'];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private offerService: OfferService,
  ) {}

  ngOnInit(): void {
    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        const navigatingToSearch = this.searchRoutes.some((route) =>
          event.url.startsWith(route),
        );
        if (!navigatingToSearch) {
          this.tags = [];
        }
      }
    });

    this.route.queryParams.subscribe((params) => {
      this.tags = getTagInfoFromUrlParams(params);
    });

    this.name.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((searchTerm) => {
          const searchString = searchTerm || '';
          if (searchString.length >= 3) {
            return this.offerService.getSearchSuggestions(searchString);
          } else {
            return new Observable<Suggestions>((observer) => {
              observer.next({ offers: [], categories: [], locations: [] });
              observer.complete();
            });
          }
        }),
      )
      .subscribe((suggestions) => {
        this.suggestions = suggestions;
      });
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  onEnter() {
    const value = this.name.value?.trim();

    if (value) {
      if (!this.tags.some((existingTag) => existingTag.name === value)) {
        this.tags.push({
          id: value,
          name: value,
          type: 'custom',
        });
      }
      this.name.setValue('');
    } else if (this.tags.length === 0) {
      this.triggerSearch(this.tags);
      return;
    }

    this.triggerSearch(this.tags);
  }

  onRemoveTag(tag: TagInfo) {
    this.tags = this.tags.filter((t) => t.id !== tag.id);
    this.triggerSearch(this.tags);
  }

  onSelectSuggestion(suggestion: Suggestion, type: string) {
    if (!this.tags.some((tag) => tag.id === suggestion.id)) {
      this.tags.push({
        id:
          type === 'postalCode'
            ? (suggestion as LocationSuggestion).plz
            : suggestion.id,
        name:
          type === 'postalCode'
            ? (suggestion as LocationSuggestion).plz
            : suggestion.name,
        type: type,
      });
    }

    this.name.setValue('');
    this.suggestions = { offers: [], categories: [], locations: [] };
    this.triggerSearch(this.tags);
  }

  onBackspace() {
    const value = this.name.value;

    if (!value && this.tags.length > 0) {
      this.tags.pop();
    }

    this.triggerSearch(this.tags);
  }

  private triggerSearch(tags: TagInfo[]) {
    const queryParams =
      tags.length > 0
        ? { tags: tags.map((t) => `${t.id}:${t.name}:${t.type}`).join(',') }
        : {};
    this.router.navigate(['/search'], { queryParams });
  }

  getTagColorClass(type: string): string {
    switch (type) {
      case 'offer':
        return 'bg-blue-100 text-blue-700';
      case 'category':
        return 'bg-green-100 text-green-700';
      case 'location':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return '';
    }
  }
}
